import { ToastOptions, ToastVariant } from './toast.types';
import { dismissAllToasts, dismissToast, dismissToastByKey, showToast } from './toastStore';

const raise =
  (variant: ToastVariant) =>
  (title: string, options: ToastOptions = {}): number =>
    showToast({ ...options, variant, title });

/**
 * The app's one way to raise a toast.
 *
 * Frozen and defined once, so it's referentially stable for free — `useToast`
 * hands back this exact object and it's safe in a dependency array. Import it
 * directly from modules that aren't components.
 *
 * ```ts
 * toast.success('Changes saved');
 * toast.info(`Applied ${location} to ${count} items`, {
 *   action: { label: 'Undo', onClick: undoLastBulkApply },
 * });
 * ```
 */
export const toast = Object.freeze({
  success: raise('success'),
  error: raise('error'),
  warning: raise('warning'),
  info: raise('info'),
  /** Escape hatch for a variant held in a variable. */
  show: showToast,
  dismiss: dismissToast,
  /** Dismiss by `dedupeKey`, for a toast whose id the caller never kept. */
  dismissKey: dismissToastByKey,
  dismissAll: dismissAllToasts,
});

export type ToastApi = typeof toast;

export {
  TOAST_DURATION,
  TOAST_EXIT_MS,
  TOAST_QUEUE_LIMIT,
  TOAST_VISIBLE_LIMIT,
} from './toast.types';
export type {
  Toast,
  ToastAction,
  ToastInput,
  ToastOptions,
  ToastStatus,
  ToastVariant,
} from './toast.types';
export {
  dismissAllToasts,
  dismissToast,
  dismissToastByKey,
  getSnapshot,
  removeToast,
  resetToastStoreForTests,
  showToast,
  subscribe,
} from './toastStore';
export { buildToastReport, copyTextToClipboard } from './toastClipboard';
export type { ToastReportContext } from './toastClipboard';
export {
  toastDefaultDuration,
  toastPoliteness,
  toastRole,
  toastVariantLabel,
} from './toastVariant';
